
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Game } from '@/types/game';
import GameCard from '../components/game/GameCard';

const TrendingPage = () => {
  // Fetch trending games from Supabase
  const { data: trendingGames = [], isLoading, error } = useQuery({
    queryKey: ['trending-games'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('games')
        .select('*')
        .eq('trending', true)
        .eq('hidden', false)
        .order('play_count', { ascending: false });
      
      if (error) throw error;
      
      // Transform to match Game type
      return (data || []).map((dbGame): Game => ({
        id: dbGame.id,
        title: dbGame.title,
        description: dbGame.description || '',
        thumbnail: dbGame.thumbnail || '',
        category: dbGame.category,
        featured: dbGame.featured || false,
        trending: dbGame.trending || false,
        isNew: dbGame.is_new || false,
        rating: Number(dbGame.rating) || 0,
        playCount: dbGame.play_count ?? 0,
        fakePlayCount: dbGame.fake_play_count,
        createdAt: dbGame.created_at,
        updatedAt: dbGame.updated_at,
        tags: dbGame.tags || [],
        embedType: 'iframe',
        gameUrl: dbGame.game_url || '',
      }));
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Trending Games</h1>
            <p className="text-gray-600">Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Trending Games - GameHub | Play the Hottest Online Games</title>
        <meta 
          name="description" 
          content="Discover the most trending games on GameHub. Play the hottest and most popular online games that everyone is talking about." 
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="p-3 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl shadow-lg">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-gray-900">
                Trending Games
              </h1>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-medium">
              🔥 Play the hottest games that everyone is talking about. These are the most popular games right now!
            </p>
          </div>

          {/* Games Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
            {trendingGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>

          {/* Empty State */}
          {trendingGames.length === 0 && (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Trending Games Yet</h3>
              <p className="text-gray-600">Check back soon for the latest trending games!</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TrendingPage;
